/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React,{useState,useEffect,useCallback}from'react';
import{useNavigate}from'react-router-dom';
import{LineChart,Line,XAxis,YAxis,CartesianGrid,Tooltip,Legend,ResponsiveContainer}from'recharts';
import Layout from'../components/layout/Layout';
import Modal from'../components/common/Modal';
import Badge from'../components/common/Badge';
import StatCard from'../components/common/StatCard';
import api from'../services/api';
import {useTranslation} from 'react-i18next';
import FileShaClaimButton from '../components/common/FileShaClaimButton';
import ShrPanel from '../components/common/ShrPanel';

const TABS=[
  ['info','Patient Info'],
  ['visits','Visit History'],
  ['problems','Problem List'],
  ['allergies','Allergies'],
  ['vitals','Vitals'],
  ['sha','SHA / Insurance'],
  ['shr','Kenya SHR'],
  ['immunizations','Immunizations'],
  ['documents','Documents'],
];

export default function PatientPage(){
  const {t}=useTranslation();
  const nav=useNavigate();
  const [search,setSearch]=useState('');
  const [patients,setPatients]=useState<any[]>([]);
  const [selected,setSelected]=useState<any>(null);
  const [tab,setTab]=useState('info');
  const [visits,setVisits]=useState<any[]>([]);
  const [problems,setProblems]=useState<any[]>([]);
  const [allergies,setAllergies]=useState<any[]>([]);
  const [vitals,setVitals]=useState<any[]>([]);
  const [docs,setDocs]=useState<any[]>([]);
  const [immunizations,setImmunizations]=useState<any[]>([]);
  const [showImmunization,setShowImmunization]=useState(false);
  const [immForm,setImmForm]=useState({vaccineName:'',doseNumber:'1',administeredDate:'',nextDueDate:'',lotNumber:'',site:'',notes:''});
  const [loading,setLoading]=useState(false);
  const [msg,setMsg]=useState('');
  const [showAllergy,setShowAllergy]=useState(false);
  const [showProblem,setShowProblem]=useState(false);
  const [shaChecking,setShaChecking]=useState(false);
  const [shaStatus,setShaStatus]=useState<any>(null);
  const [allergyForm,setAllergyForm]=useState({allergenName:'',allergenType:'DRUG',severity:'MODERATE',reaction:''});
  const [problemForm,setProblemForm]=useState({problemName:'',icdCode:'',status:'ACTIVE',notes:''});
  const [stats,setStats]=useState({total:0,today:0,admitted:0,critical:0});
  const notify=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(''),4000);};

  const arr=(r:any)=>Array.isArray(r?.data)?r.data:r?.data?.data||r?.data?.items||r?.data?.patients||r?.data||[];

  // Load stats
  useEffect(()=>{
    (async()=>{
      try{
        const [s,t]=await Promise.all([
          api.getPatientStats?.().catch(()=>({data:{}})),
          api.getTodayPatients?.().catch(()=>({data:{}})),
        ]);
        setStats({
          total:s?.data?.total||0,
          today:t?.data?.count||t?.data?.todayRegistrations||0,
          admitted:0,critical:0,
        });
      }catch{}
    })();
  },[]);

  // Load latest patients
  useEffect(()=>{
    (async()=>{
      setLoading(true);
      try{
        const r=await api.getPatients?.({take:40,skip:0}).catch(()=>({data:{}}));
        setPatients(arr(r));
      }catch{}
      setLoading(false);
    })();
  },[]);

  const doSearch=useCallback(async()=>{
    setLoading(true);
    try{
      const r=await api.getPatients?.({search:search.trim()||undefined,take:40}).catch(()=>({data:{}}));
      setPatients(arr(r));
    }catch{}
    setLoading(false);
  },[search]);

  const loadPatient=async(p:any)=>{
    setSelected(p);setTab('info');setShaStatus(null);
    try{
      const [v,pr,al,vt,d,im]=await Promise.all([
        api.getPatientVisits?.(p.id).catch(()=>({data:[]})),
        api.getPatientProblems?.(p.id).catch(()=>({data:[]})),
        api.getPatientAllergies?.(p.id).catch(()=>({data:[]})),
        api.getVitals?.(p.id).catch(()=>({data:[]})),
        api.getPatientDocuments?.(p.id).catch(()=>({data:[]})),
        api.getImmunizations?.(p.id).catch(()=>({data:[]})),
      ]);
      setVisits(arr(v));setProblems(arr(pr));setAllergies(arr(al));setVitals(arr(vt));setDocs(arr(d));setImmunizations(arr(im));
    }catch{}
  };

  const addAllergy=async()=>{
    if(!selected||!allergyForm.allergenName)return;
    try{
      await api.addPatientAllergy?.(selected.id,allergyForm);
      notify('Allergy saved');setShowAllergy(false);
      const r=await api.getPatientAllergies?.(selected.id).catch(()=>({data:[]}));
      setAllergies(arr(r));
    }catch(e:any){notify('Error: '+(e.response?.data?.message||'failed'));}
  };

  const addProblem=async()=>{
    if(!selected||!problemForm.problemName)return;
    try{
      await api.addPatientProblem?.(selected.id,problemForm);
      notify('Problem saved');setShowProblem(false);
      const r=await api.getPatientProblems?.(selected.id).catch(()=>({data:[]}));
      setProblems(arr(r));
    }catch(e:any){notify('Error: '+(e.response?.data?.message||'failed'));}
  };

  // SHA eligibility check from patient dashboard (PRD 1.6.1)
  const checkSHA=async()=>{
    if(!selected)return;
    const idNo=selected.nationalId||selected.national_id||selected.passportNumber||selected.passport_number;
    if(!idNo){setShaStatus({error:true,message:'No National ID on record for this patient'});return;}
    setShaChecking(true);setShaStatus(null);
    try{
      const m=await import('../services/sha.api');
      const idType=selected.kenyanIdType||selected.kenyan_id_type||'NATIONAL_ID';
      const ID_MAP:Record<string,string>={'NATIONAL_ID':'National ID','PASSPORT':'Passport','ALIEN_ID':'Alien ID','REFUGEE_ID':'Refugee ID','BIRTH_CERT':'Birth Certificate','HUDUMA_NUMBER':'Mandate Number'};
      const r=await m.shaCheckEligibility({identificationNumber:idNo,identificationType:ID_MAP[idType]||'National ID',serviceType:'OUTPATIENT'});
      setShaStatus(r?.data);
    }catch(e:any){setShaStatus({error:true,message:e?.response?.data?.message||'SHA check failed'});}
    setShaChecking(false);
  };

  // Vitals chart data
  const vitalsChartData=vitals.slice().reverse().map((v:any,i:number)=>({
    time:v.recorded_at?new Date(v.recorded_at).toLocaleDateString('en-KE',{month:'short',day:'numeric'}):`#${i+1}`,
    Systolic:v.systolic_bp||v.systolic||null,
    Diastolic:v.diastolic_bp||v.diastolic||null,
    Pulse:v.pulse||v.heart_rate||null,
    Temp:v.temperature||null,
    SpO2:v.spo2||v.oxygen_saturation||null,
    Weight:v.weight_kg||v.weight||null,
  }));

  return(
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div><h1 className="text-2xl font-bold text-gray-900">Patient Management</h1><p className="text-gray-500">Search and manage patient records</p></div>
          <button onClick={()=>nav('/patients/register')} className="bg-[#02357A] text-white px-4 py-2 rounded-lg hover:bg-[#015CA6] font-medium">+ Register Patient</button>
        </div>

        {msg&&<div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">{msg}</div>}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatCard label="Total Patients" value={stats.total} color="blue"/>
          <StatCard label="Today Registered" value={stats.today} color="green"/>
          <StatCard label="Currently Admitted" value={stats.admitted} color="orange"/>
          <StatCard label="Critical" value={stats.critical} color="red"/>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
          <div className="flex gap-3">
            <input value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={e=>e.key==='Enter'&&doSearch()}
              placeholder="Search by name, UHID, phone, National ID..." className="flex-1 border rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#015CA6] outline-none text-sm"/>
            <button onClick={doSearch} className="bg-[#02357A] text-white px-6 py-2 rounded-lg hover:bg-[#015CA6] text-sm font-medium">{loading?'Searching...':'Search'}</button>
            <button onClick={()=>nav('/patients/register')} className="border border-[#015CA6] text-[#02357A] px-4 py-2 rounded-lg hover:bg-blue-50 text-sm font-medium">+ New</button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Patient List */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-4 border-b bg-gray-50">
              <h3 className="font-semibold text-gray-700 text-sm">{search.trim()?`Results (${patients.length})`:`Latest Patients (${patients.length})`}</h3>
            </div>
            <div className="divide-y overflow-y-auto" style={{maxHeight:'70vh'}}>
              {patients.length===0&&<div className="p-8 text-center text-gray-400 text-sm">No patients found</div>}
              {patients.map((p:any)=>(
                <div key={p.id} onClick={()=>loadPatient(p)} className={`p-4 cursor-pointer hover:bg-blue-50 transition-colors ${selected?.id===p.id?'bg-blue-50 border-l-4 border-[#015CA6]':''}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{p.firstName||p.first_name} {p.lastName||p.last_name}</p>
                      <p className="text-xs text-[#02357A] font-mono">{p.uhid}</p>
                      <p className="text-xs text-gray-500">{p.gender} · {p.mobile||p.phone||'—'}</p>
                      {(p.shaBeneficiaryCrId||p.sha_beneficiary_cr_id)&&
                  <span className="text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-lg">🛡️ SHA/SHIF</span>}
                {p.insuranceProvider&&
                  <span className="text-xs text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-lg">🏥 {p.insuranceProvider}</span>}
                {!p.insuranceProvider&&!(p.shaBeneficiaryCrId||p.sha_beneficiary_cr_id)&&
                  <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-lg">💵 Cash</span>}
                    </div>
                    <Badge text={p.bloodGroup||p.blood_group||'—'} color="gray"/>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Patient Detail */}
          <div className="col-span-2 bg-white rounded-xl shadow-sm border overflow-hidden">
            {!selected?(
              <div className="flex items-center justify-center h-96 text-gray-400">
                <div className="text-center"><div className="text-5xl mb-3">👤</div><p>Select a patient to view details</p></div>
              </div>
            ):(
              <>
                {/* Patient Header */}
                <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-blue-200 flex items-center justify-center text-2xl font-bold text-[#02357A]">
                        {(selected.firstName||selected.first_name||'?')[0].toUpperCase()}
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">{selected.firstName||selected.first_name} {selected.middleName||selected.middle_name||''} {selected.lastName||selected.last_name}</h2>
                        <p className="text-[#02357A] font-mono font-semibold text-sm">{selected.uhid}</p>
                        <div className="flex gap-3 mt-1">
                          <p className="text-sm text-gray-600">{selected.gender} · DOB: {(selected.dateOfBirth||selected.date_of_birth||'—').split('T')[0]}</p>
                          {(selected.bloodGroup||selected.blood_group)&&<p className="text-sm text-gray-600">Blood: {selected.bloodGroup||selected.blood_group}</p>}
                          {(selected.shaBeneficiaryCrId||selected.sha_beneficiary_cr_id)&&<span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-semibold">SHA</span>}
                          {allergies.length>0&&<span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-semibold">⚠️ Allergies</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap justify-end">
                      <button onClick={()=>nav(`/patients/edit/${selected.id}`)} className="border px-3 py-1.5 rounded-lg text-xs hover:bg-gray-50 font-medium">✏️ Edit</button>
                      <button onClick={()=>nav(`/appointments/book?patientId=${selected.id}`)} className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs hover:bg-green-700 font-medium">📅 Book Appt</button>
                      <button onClick={()=>nav(`/ipd/admit?patientId=${selected.id}`)} className="bg-purple-600 text-white px-3 py-1.5 rounded-lg text-xs hover:bg-purple-700 font-medium">🛏️ Admit IPD</button>
                      {/* <button onClick={()=>nav(`/opd?patientId=${selected.id}`)} className="bg-[#02357A] text-white px-3 py-1.5 rounded-lg text-xs hover:bg-[#015CA6] font-medium">👨‍⚕️ OPD</button> */}
                    </div>
                  </div>
                  {/* Tabs */}
                  <div className="flex gap-1 mt-4 flex-wrap">
                    {TABS.map(([k,l])=>(
                      <button key={k} onClick={()=>setTab(k)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${tab===k?'bg-[#02357A] text-white':'text-gray-600 hover:bg-white hover:text-[#02357A]'}`}>{l}</button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-4 overflow-y-auto" style={{maxHeight:'calc(70vh - 180px)'}}>

                  {/* INFO TAB */}
                  {tab==='info'&&(
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        ['Phone',selected.mobile||selected.phone||'—'],
                        ['Email',selected.email||'—'],
                        ['Address',selected.addressLine1||selected.address_line1||selected.address||'—'],
                        ['City',selected.city||'—'],
                        ['County',selected.county||selected.state||'—'],
                        ['Sub-County',selected.subCounty||selected.sub_county||'—'],
                        ['Ward',selected.ward||'—'],
                        ['Postal Code',selected.postalCode||selected.postal_code||'—'],
                        ['Nationality',selected.nationality||'—'],
                        ['Religion',selected.religion||'—'],
                        ['Occupation',selected.occupation||'—'],
                        ['Marital Status',selected.maritalStatus||selected.marital_status||'—'],
                        ['Father Name',selected.fatherName||selected.father_name||'—'],
                        ['Mother Name',selected.motherName||selected.mother_name||'—'],
                        ['Insurance',selected.insuranceProvider||selected.insurance_provider||'—'],
                        ['NHIF No',selected.nhifNumber||selected.nhif_number||'—'],
                        ['National ID',selected.nationalId||selected.national_id||'—'],
                        ['SHA CR ID',selected.shaBeneficiaryCrId||selected.sha_beneficiary_cr_id||'—'],
                      ].map(([l,v])=>(
                        <div key={l} className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 uppercase tracking-wide">{l}</p>
                          <p className="font-medium text-gray-900 mt-0.5 text-sm">{v}</p>
                        </div>
                      ))}
                      {(selected.knownAllergies||selected.known_allergies)&&(
                        <div className="col-span-2 bg-red-50 rounded-lg p-3 border border-red-200">
                          <p className="text-xs text-red-500 uppercase tracking-wide">⚠️ Known Allergies</p>
                          <p className="font-medium text-red-700 mt-0.5 text-sm">{selected.knownAllergies||selected.known_allergies}</p>
                        </div>
                      )}
                      {(selected.chronicConditions||selected.chronic_conditions)&&(
                        <div className="col-span-2 bg-orange-50 rounded-lg p-3 border border-orange-200">
                          <p className="text-xs text-orange-500 uppercase tracking-wide">Chronic Conditions</p>
                          <p className="font-medium text-orange-700 mt-0.5 text-sm">{selected.chronicConditions||selected.chronic_conditions}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* VISITS TAB */}
                  {tab==='visits'&&(
                    <div>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-semibold text-sm">Visit History ({visits.length})</h4>
                        <button onClick={()=>nav(`/opd?patientId=${selected.id}`)} className="text-xs bg-[#02357A] text-white px-3 py-1.5 rounded-lg">+ New OPD Visit</button>
                      </div>
                      {visits.length===0?<p className="text-center text-gray-400 py-8 text-sm">No visits yet</p>:(
                        <table className="w-full text-sm"><thead className="bg-gray-50">
                          <tr>{['Date','Type','Doctor','Status'].map(h=><th key={h} className="text-left p-3 text-gray-600 font-medium text-xs">{h}</th>)}</tr></thead>
                          <tbody className="divide-y">{visits.map((v:any,i:number)=>(
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="p-3 text-xs">{(v.visitDate||v.visit_date||v.createdAt||'')?.split('T')[0]||'—'}</td>
                              <td className="p-3"><Badge text={v.visitType||v.visit_type||'OPD'} color="blue"/></td>
                              <td className="p-3 text-xs">{v.doctorName||v.doctor_name||'—'}</td>
                              <td className="p-3"><Badge text={v.status||'COMPLETED'} color="green"/></td>
                            </tr>
                          ))}</tbody>
                        </table>
                      )}
                    </div>
                  )}

                  {/* PROBLEMS TAB */}
                  {tab==='problems'&&(
                    <div>
                      <div className="flex justify-between mb-3">
                        <h4 className="font-semibold text-sm">Problem List ({problems.length})</h4>
                        <button onClick={()=>setShowProblem(true)} className="text-xs bg-orange-600 text-white px-3 py-1.5 rounded-lg">+ Add Problem</button>
                      </div>
                      {problems.length===0?<p className="text-center text-gray-400 py-8 text-sm">No active problems</p>:(
                        <div className="space-y-2">{problems.map((pr:any,i:number)=>(
                          <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                            <div>
                              <p className="font-medium text-sm">{pr.problemName||pr.problem_name}</p>
                              <p className="text-xs text-gray-500">{pr.icdCode||pr.icd_code||''} · Since: {(pr.onsetDate||pr.onset_date||'—').split('T')[0]}</p>
                            </div>
                            <Badge text={pr.status||'ACTIVE'} color={pr.status==='RESOLVED'?'green':'orange'}/>
                          </div>
                        ))}</div>
                      )}
                    </div>
                  )}

                  {/* ALLERGIES TAB */}
                  {tab==='allergies'&&(
                    <div>
                      <div className="flex justify-between mb-3">
                        <h4 className="font-semibold text-sm">Allergies ({allergies.length})</h4>
                        <button onClick={()=>setShowAllergy(true)} className="text-xs bg-red-600 text-white px-3 py-1.5 rounded-lg">+ Add Allergy</button>
                      </div>
                      {allergies.length===0?<p className="text-center text-gray-400 py-8 text-sm">No allergies recorded</p>:(
                        <div className="space-y-2">{allergies.map((a:any,i:number)=>(
                          <div key={i} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                            <div>
                              <p className="font-semibold text-red-700 text-sm">{a.allergenName||a.allergen_name||a.allergen}</p>
                              <p className="text-xs text-gray-600">{a.allergenType||a.allergen_type||a.type} · Reaction: {a.reaction||'—'}</p>
                            </div>
                            <Badge text={a.severity||'UNKNOWN'} color={a.severity==='SEVERE'?'red':a.severity==='MODERATE'?'orange':'yellow'}/>
                          </div>
                        ))}</div>
                      )}
                    </div>
                  )}

                  {/* VITALS TAB — PRD 1.6.3 line graph */}
                  {tab==='vitals'&&(
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-sm">Vitals History ({vitals.length} records)</h4>
                        <button onClick={()=>nav(`/nursing?patientId=${selected.id}`)} className="text-xs bg-[#039089] text-white px-3 py-1.5 rounded-lg">+ Record Vitals</button>
                      </div>
                      {vitals.length===0?<p className="text-center text-gray-400 py-8 text-sm">No vitals recorded</p>:(
                        <>
                          {/* Blood Pressure & Pulse Graph */}
                          {vitals.length>1&&(
                            <div className="mb-5">
                              <p className="text-xs font-bold text-slate-500 uppercase mb-2">Blood Pressure & Pulse Trend</p>
                              <ResponsiveContainer width="100%" height={200}>
                                <LineChart data={vitalsChartData}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                  <XAxis dataKey="time" tick={{fontSize:11}}/>
                                  <YAxis tick={{fontSize:11}}/>
                                  <Tooltip/>
                                  <Legend iconSize={10} wrapperStyle={{fontSize:11}}/>
                                  <Line type="monotone" dataKey="Systolic" stroke="#ef4444" strokeWidth={2} dot={{r:3}} name="Systolic (mmHg)"/>
                                  <Line type="monotone" dataKey="Diastolic" stroke="#f97316" strokeWidth={2} dot={{r:3}} name="Diastolic (mmHg)"/>
                                  <Line type="monotone" dataKey="Pulse" stroke="#3b82f6" strokeWidth={2} dot={{r:3}} name="Pulse (bpm)"/>
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          )}
                          {/* SpO2 & Temp Graph */}
                          {vitals.length>1&&(
                            <div className="mb-5">
                              <p className="text-xs font-bold text-slate-500 uppercase mb-2">SpO2 & Temperature Trend</p>
                              <ResponsiveContainer width="100%" height={160}>
                                <LineChart data={vitalsChartData}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                  <XAxis dataKey="time" tick={{fontSize:11}}/>
                                  <YAxis tick={{fontSize:11}}/>
                                  <Tooltip/>
                                  <Legend iconSize={10} wrapperStyle={{fontSize:11}}/>
                                  <Line type="monotone" dataKey="SpO2" stroke="#10b981" strokeWidth={2} dot={{r:3}} name="SpO2 (%)"/>
                                  <Line type="monotone" dataKey="Temp" stroke="#8b5cf6" strokeWidth={2} dot={{r:3}} name="Temp (°C)"/>
                                </LineChart>
                              </ResponsiveContainer>
                            </div>
                          )}
                          {/* Vitals Table */}
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50">
                                <tr>{['Date/Time','BP','Pulse','Temp','SpO2','Weight','RR'].map(h=><th key={h} className="text-left p-2 text-gray-600 font-medium">{h}</th>)}</tr>
                              </thead>
                              <tbody className="divide-y">
                                {vitals.slice(0,15).map((v:any,i:number)=>(
                                  <tr key={i} className="hover:bg-gray-50">
                                    <td className="p-2">{(v.recordedAt||v.recorded_at||'').replace('T',' ').substring(0,16)||'—'}</td>
                                    <td className="p-2 font-medium">{v.systolicBp||v.systolic_bp||v.systolic}{(v.systolicBp||v.systolic_bp||v.systolic)&&v.diastolicBp||v.diastolic_bp||v.diastolic?'/':''}{v.diastolicBp||v.diastolic_bp||v.diastolic||(!v.systolicBp&&!v.systolic_bp&&!v.systolic?'—':'')}</td>
                                    <td className="p-2">{v.pulse||v.heart_rate||'—'}</td>
                                    <td className="p-2">{v.temperature?`${v.temperature}°C`:'—'}</td>
                                    <td className="p-2">{v.spo2?`${v.spo2}%`:'—'}</td>
                                    <td className="p-2">{v.weightKg||v.weight_kg||v.weight?`${v.weightKg||v.weight_kg||v.weight}kg`:'—'}</td>
                                    <td className="p-2">{v.respiratoryRate||v.respiratory_rate||'—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* SHA / INSURANCE TAB — PRD 1.6.1 */}
                  {tab==='sha'&&(
                    <div className="space-y-4">
                      <div className="bg-teal-50 rounded-xl border border-teal-200 p-4">
                        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                          <div>
                            <p className="font-bold text-teal-800">SHA / SHIF Eligibility</p>
                            <p className="text-xs text-teal-600 mt-0.5">
                              CR ID: <span className="font-mono font-bold">{selected.shaBeneficiaryCrId||selected.sha_beneficiary_cr_id||'Not registered'}</span>
                            </p>
                          </div>
                          <button onClick={checkSHA} disabled={shaChecking} className="bg-[#039089] hover:bg-teal-700 text-white font-bold py-2 px-4 rounded-xl text-sm disabled:opacity-50">
                            {shaChecking?'⟳ Checking...':'🔍 Check SHA Eligibility'}
                          </button>
                          <FileShaClaimButton
                            patientId={selected.id}
                            patientName={`${selected.firstName||selected.first_name||''} ${selected.lastName||selected.last_name||''}`.trim()}
                            patientCrId={selected.shaBeneficiaryCrId||selected.sha_beneficiary_cr_id}
                            encounterId={visits[0]?.id||visits[0]?.reference_id||selected.id}
                            serviceName="Outpatient consultation"
                            from="opd"
                          />
                        </div>
                        {!(selected.shaBeneficiaryCrId||selected.sha_beneficiary_cr_id)&&(
                          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2">
                            ⚠️ No SHA CR ID on record. <button onClick={()=>nav(`/patients/edit/${selected.id}`)} className="underline font-semibold">Update patient →</button>
                          </p>
                        )}
                        {shaStatus&&!shaStatus.error&&(()=>{
                          const isEligible=shaStatus.schemes?.some((s:any)=>s?.coverage?.status==='1')||shaStatus.statusCode==='10';
                          return(
                            <div className={`mt-3 p-3 rounded-xl border-2 space-y-2 ${isEligible?'bg-emerald-50 border-emerald-300':'bg-red-50 border-red-200'}`}>
                              <p className={`font-black ${isEligible?'text-emerald-700':'text-red-600'}`}>{isEligible?'✅ SHA ELIGIBLE':'❌ NOT ELIGIBLE'}</p>
                              {(shaStatus.schemes||[]).map((s:any,i:number)=>{
                                const covered=s.coverage?.status==='1';
                                return<div key={i} className={`flex items-center justify-between rounded-xl px-3 py-2 text-xs border ${covered?'bg-white border-emerald-200':'bg-red-50 border-red-200'}`}>
                                  <div><p className="font-bold">{s.schemeName}</p><p className="text-slate-500">{s.memberType}</p></div>
                                  <span className={`px-2 py-0.5 rounded-full font-bold ${covered?'bg-emerald-100 text-emerald-700':'bg-red-100 text-red-600'}`}>{covered?'COVERED':'NOT COVERED'}</span>
                                </div>;
                              })}
                            </div>
                          );
                        })()}
                        {shaStatus?.error&&<div className="mt-3 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">❌ {shaStatus.message}</div>}
                        <p className="text-xs text-teal-700 mt-3">Open the SHA wizard from here after eligibility. Existing claims appear as “Open SHA Claim” if this encounter already has one.</p>
                      </div>
                      {/* Other Insurance */}
                      <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                        <p className="font-bold text-blue-800 mb-2">Other Insurance</p>
                        {(selected.insuranceProvider||selected.insurance_provider)?(
                          <div className="space-y-1">
                            <p className="text-sm"><span className="text-slate-500">Provider:</span> <span className="font-semibold">{selected.insuranceProvider||selected.insurance_provider}</span></p>
                            <p className="text-sm"><span className="text-slate-500">Policy No:</span> <span className="font-mono">{selected.insurancePolicyNumber||selected.insurance_policy_number||'—'}</span></p>
                            <p className="text-sm"><span className="text-slate-500">Expiry:</span> {selected.insuranceExpiry||selected.insurance_expiry||'—'}</p>
                            <p className="text-sm"><span className="text-slate-500">NHIF No:</span> <span className="font-mono">{selected.nhifNumber||selected.nhif_number||'—'}</span></p>
                          </div>
                        ):<p className="text-sm text-slate-400">No other insurance on record</p>}
                        <button onClick={()=>nav(`/patients/edit/${selected.id}`)} className="mt-3 text-xs text-blue-600 hover:underline">✏️ Update insurance details →</button>
                      </div>
                    </div>
                  )}

                  {tab==='shr'&& selected.id && (
                    <ShrPanel patientId={selected.id} encounterId={visits[0]?.id||visits[0]?.reference_id} visitType="OUTPATIENT" />
                  )}

                  {/* IMMUNIZATIONS TAB — PRD 1.6.2 */}
                  {tab==='immunizations'&&(
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-sm">Immunization Record ({immunizations.length})</h4>
                        <button onClick={()=>setShowImmunization(true)} className="text-xs bg-[#02357A] text-white px-3 py-1.5 rounded-lg">+ Record Vaccine</button>
                      </div>
                      {immunizations.length===0?(
                        <div className="text-center py-10 text-gray-400">
                          <div className="text-4xl mb-2">💉</div>
                          <p className="text-sm">No immunizations recorded yet</p>
                          <p className="text-xs mt-1">Click + Record Vaccine to add</p>
                        </div>
                      ):(
                        <div className="space-y-2">
                          {immunizations.map((imm:any,i:number)=>(
                            <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                              <div>
                                <p className="font-medium text-sm">{imm.vaccineName||imm.vaccine_name||imm.name}</p>
                                <div className="flex gap-3 text-xs text-gray-500 mt-0.5">
                                  <span>Dose {imm.doseNumber||imm.dose_number||'1'}</span>
                                  {(imm.administeredDate||imm.administered_date)&&<span>Given: {(imm.administeredDate||imm.administered_date)?.split('T')[0]}</span>}
                                  {(imm.nextDueDate||imm.next_due_date)&&<span>Next due: {(imm.nextDueDate||imm.next_due_date)?.split('T')[0]}</span>}
                                  {(imm.lotNumber||imm.lot_number)&&<span>Lot: {imm.lotNumber||imm.lot_number}</span>}
                                </div>
                                {imm.notes&&<p className="text-xs text-gray-400 mt-0.5 italic">{imm.notes}</p>}
                              </div>
                              <Badge text={imm.status||'GIVEN'} color={!imm.status||imm.status==='GIVEN'?'green':imm.status==='PARTIAL'?'orange':'gray'}/>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* DOCUMENTS TAB */}
                  {tab==='documents'&&(
                    <div>
                      <h4 className="font-semibold text-sm mb-3">Documents ({docs.length})</h4>
                      {docs.length===0?<p className="text-center text-gray-400 py-8 text-sm">No documents uploaded</p>:(
                        <div className="grid grid-cols-2 gap-3">
                          {docs.map((d:any,i:number)=>(
                            <div key={i} className="p-3 border rounded-lg hover:shadow-sm">
                              <p className="font-medium text-sm">{d.documentName||d.document_name||d.originalName||d.name||'Document'}</p>
                              <p className="text-xs text-gray-500">{d.documentType||d.document_type||'—'} · {(d.createdAt||d.created_at||'').split('T')[0]||'—'}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Add Allergy Modal */}
        <Modal isOpen={showAllergy} onClose={()=>setShowAllergy(false)} title="Add Allergy">
          <div className="space-y-4">
            <div><label className="block text-sm font-medium mb-1">Allergen Name *</label><input value={allergyForm.allergenName} onChange={e=>setAllergyForm(f=>({...f,allergenName:e.target.value}))} className="w-full border rounded-lg p-2 text-sm"/></div>
            <div><label className="block text-sm font-medium mb-1">{t('common.type','Type')}</label><select value={allergyForm.allergenType} onChange={e=>setAllergyForm(f=>({...f,allergenType:e.target.value}))} className="w-full border rounded-lg p-2 text-sm">{['DRUG','FOOD','ENVIRONMENT','LATEX','CONTRAST','OTHER'].map(o=><option key={o}>{o}</option>)}</select></div>
            <div><label className="block text-sm font-medium mb-1">Severity</label><select value={allergyForm.severity} onChange={e=>setAllergyForm(f=>({...f,severity:e.target.value}))} className="w-full border rounded-lg p-2 text-sm">{['MILD','MODERATE','SEVERE','LIFE_THREATENING'].map(o=><option key={o}>{o}</option>)}</select></div>
            <div><label className="block text-sm font-medium mb-1">Reaction</label><textarea value={allergyForm.reaction} onChange={e=>setAllergyForm(f=>({...f,reaction:e.target.value}))} className="w-full border rounded-lg p-2 text-sm" rows={2}/></div>
            <div className="flex gap-2 justify-end"><button onClick={()=>setShowAllergy(false)} className="px-4 py-2 border rounded-lg text-sm">{t('common.cancel','Cancel')}</button><button onClick={addAllergy} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold">Save Allergy</button></div>
          </div>
        </Modal>

        {/* Add Immunization Modal */}
        <Modal isOpen={showImmunization} onClose={()=>setShowImmunization(false)} title="Record Vaccination">
          <div className="space-y-3">
            <div><label className="block text-sm font-medium mb-1">Vaccine Name *</label>
              <select value={immForm.vaccineName} onChange={e=>setImmForm(f=>({...f,vaccineName:e.target.value}))} className="w-full border rounded-lg p-2 text-sm">
                <option value="">Select vaccine...</option>
                {['BCG','OPV (Oral Polio)','DPT-HepB-Hib','PCV10','Rotavirus','Measles-Rubella','Yellow Fever','Tetanus Toxoid (TT)','COVID-19','HPV','Hepatitis B','Typhoid','Meningococcal','Rabies','Other'].map(v=><option key={v}>{v}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-medium mb-1">Dose Number</label>
                <select value={immForm.doseNumber} onChange={e=>setImmForm(f=>({...f,doseNumber:e.target.value}))} className="w-full border rounded-lg p-2 text-sm">
                  {['1','2','3','4','5','Booster'].map(d=><option key={d}>{d}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Date Given *</label>
                <input type="date" value={immForm.administeredDate} onChange={e=>setImmForm(f=>({...f,administeredDate:e.target.value}))} className="w-full border rounded-lg p-2 text-sm"/>
              </div>
              <div><label className="block text-sm font-medium mb-1">Next Due Date</label>
                <input type="date" value={immForm.nextDueDate} onChange={e=>setImmForm(f=>({...f,nextDueDate:e.target.value}))} className="w-full border rounded-lg p-2 text-sm"/>
              </div>
              <div><label className="block text-sm font-medium mb-1">Site</label>
                <select value={immForm.site} onChange={e=>setImmForm(f=>({...f,site:e.target.value}))} className="w-full border rounded-lg p-2 text-sm">
                  <option value="">Select site</option>
                  {['Left Arm','Right Arm','Left Thigh','Right Thigh','Oral','Intranasal'].map(s=><option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-span-2"><label className="block text-sm font-medium mb-1">Lot / Batch Number</label>
                <input value={immForm.lotNumber} onChange={e=>setImmForm(f=>({...f,lotNumber:e.target.value}))} className="w-full border rounded-lg p-2 text-sm" placeholder="Vaccine batch number"/>
              </div>
            </div>
            <div><label className="block text-sm font-medium mb-1">Notes / Observations</label>
              <textarea value={immForm.notes} onChange={e=>setImmForm(f=>({...f,notes:e.target.value}))} className="w-full border rounded-lg p-2 text-sm" rows={2} placeholder="Any reactions, adverse effects..."/>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={()=>setShowImmunization(false)} className="px-4 py-2 border rounded-lg text-sm">{t('common.cancel','Cancel')}</button>
              <button onClick={async()=>{
                if(!selected||!immForm.vaccineName||!immForm.administeredDate){notify('Vaccine name and date required');return;}
                try{
                  await api.addImmunization?.({...immForm,patientId:selected.id});
                  notify('✅ Vaccination recorded');
                  setShowImmunization(false);
                  setImmForm({vaccineName:'',doseNumber:'1',administeredDate:'',nextDueDate:'',lotNumber:'',site:'',notes:''});
                  const r=await api.getImmunizations?.(selected.id).catch(()=>({data:[]}));
                  setImmunizations(arr(r));
                }catch(e:any){notify('Error: '+(e.response?.data?.message||'failed'));}
              }} className="px-4 py-2 bg-[#02357A] text-white rounded-lg text-sm font-semibold">Save Record</button>
            </div>
          </div>
        </Modal>

        {/* Add Problem Modal */}
        <Modal isOpen={showProblem} onClose={()=>setShowProblem(false)} title="Add Problem">
          <div className="space-y-4">
            <div><label className="block text-sm font-medium mb-1">Problem Name *</label><input value={problemForm.problemName} onChange={e=>setProblemForm(f=>({...f,problemName:e.target.value}))} className="w-full border rounded-lg p-2 text-sm" placeholder="e.g. Hypertension"/></div>
            <div><label className="block text-sm font-medium mb-1">ICD-11 Code</label><input value={problemForm.icdCode} onChange={e=>setProblemForm(f=>({...f,icdCode:e.target.value}))} className="w-full border rounded-lg p-2 text-sm" placeholder="e.g. BA00 (ICD-11)"/></div>
            <div><label className="block text-sm font-medium mb-1">{t('common.status','Status')}</label><select value={problemForm.status} onChange={e=>setProblemForm(f=>({...f,status:e.target.value}))} className="w-full border rounded-lg p-2 text-sm">{['ACTIVE','RESOLVED','CHRONIC','RECURRENT'].map(o=><option key={o}>{o}</option>)}</select></div>
            <div><label className="block text-sm font-medium mb-1">{t('common.notes','Notes')}</label><textarea value={problemForm.notes} onChange={e=>setProblemForm(f=>({...f,notes:e.target.value}))} className="w-full border rounded-lg p-2 text-sm" rows={2}/></div>
            <div className="flex gap-2 justify-end"><button onClick={()=>setShowProblem(false)} className="px-4 py-2 border rounded-lg text-sm">{t('common.cancel','Cancel')}</button><button onClick={addProblem} className="px-4 py-2 bg-[#02357A] text-white rounded-lg text-sm font-semibold">Save Problem</button></div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
}
